/* Get Zela — FAQ hub: category tabs, live search, single-open accordions. */

class FaqHub extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.faq-hub__tab'));
    this.panels = Array.from(this.querySelectorAll('.faq-hub__panel'));
    this.items = Array.from(this.querySelectorAll('.faq-hub__item'));
    this.search = this.querySelector('.faq-hub__search-input');
    this.clear = this.querySelector('.faq-hub__search-clear');
    this.empty = this.querySelector('.faq-hub__empty');

    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab.dataset.category)));

    this.querySelectorAll('.faq-hub__question').forEach((button) => {
      button.addEventListener('click', () => this.toggle(button));
    });

    if (this.search) {
      this.search.addEventListener('input', () => this.filter(this.search.value));
      this.search.addEventListener('search', () => this.filter(this.search.value));
    }

    if (this.clear) {
      this.clear.addEventListener('click', () => {
        this.search.value = '';
        this.filter('');
        this.search.focus();
      });
    }

    this.querySelectorAll('.faq-hub__answer').forEach((answer) => answer.setAttribute('inert', ''));

    if (this.tabs.length) this.selectTab(this.tabs[0].dataset.category);
    this.openFromHash();
    window.addEventListener('hashchange', () => this.openFromHash());
  }

  selectTab(category) {
    this.tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.category === category)));
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.category !== category;
    });
  }

  toggle(button) {
    const expanded = button.getAttribute('aria-expanded') === 'true';

    if (!expanded && this.dataset.singleOpen === 'true') {
      this.querySelectorAll('.faq-hub__question[aria-expanded="true"]').forEach((open) => this.close(open));
    }

    if (expanded) this.close(button);
    else this.open(button);
  }

  open(button) {
    const answer = document.getElementById(button.getAttribute('aria-controls'));
    if (!answer) return;
    button.setAttribute('aria-expanded', 'true');
    answer.removeAttribute('inert');
    answer.style.maxHeight = `${answer.scrollHeight}px`;
    answer.addEventListener(
      'transitionend',
      () => {
        if (button.getAttribute('aria-expanded') === 'true') answer.style.maxHeight = 'none';
      },
      { once: true }
    );
  }

  close(button) {
    const answer = document.getElementById(button.getAttribute('aria-controls'));
    if (!answer) return;
    button.setAttribute('aria-expanded', 'false');
    answer.setAttribute('inert', '');
    answer.style.maxHeight = `${answer.scrollHeight}px`;
    requestAnimationFrame(() => {
      answer.style.maxHeight = '0px';
    });
  }

  filter(rawTerm) {
    const term = rawTerm.trim().toLowerCase();

    if (this.clear) this.clear.hidden = term === '';

    if (term === '') {
      this.classList.remove('is-searching');
      this.items.forEach((item) => {
        item.hidden = false;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.category !== this.selectedCategory();
      });
      if (this.empty) this.empty.hidden = true;
      return;
    }

    this.classList.add('is-searching');

    let matches = 0;
    this.items.forEach((item) => {
      const hit = item.dataset.searchText.includes(term);
      item.hidden = !hit;
      if (hit) matches += 1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = !panel.querySelector('.faq-hub__item:not([hidden])');
    });

    if (this.empty) this.empty.hidden = matches !== 0;
  }

  selectedCategory() {
    const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    return active ? active.dataset.category : this.tabs.length ? this.tabs[0].dataset.category : '';
  }

  openFromHash() {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const button = this.querySelector(`.faq-hub__question[data-anchor="${CSS.escape(id)}"]`);
    if (!button) return;
    const panel = button.closest('.faq-hub__panel');
    if (panel) this.selectTab(panel.dataset.category);
    this.open(button);
    button.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

if (!customElements.get('faq-hub')) customElements.define('faq-hub', FaqHub);
